import { FileText, Download, ShieldCheck, FlaskConical, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";

const DOCS = [
  {
    id: "coa",
    icon: FlaskConical,
    title: "Certificate of Analysis",
    code: "COA",
    desc: "Master specification sheets for all 19 product lines — moisture, ash, microbiology and heavy-metal limits, referenced to FSSAI standards.",
    file: "/documents/MSD_Foods_COA.docx",
    meta: "19 specs · FSSAI referenced",
  },
  {
    id: "msds",
    icon: ShieldCheck,
    title: "Safety Data Sheet",
    code: "MSDS",
    desc: "16-section safety documentation per product — handling, storage, first-aid and transport guidance for food-grade powders and caramel colours.",
    file: "/documents/MSD_Foods_MSDS.docx",
    meta: "16 sections · Rev 01",
  },
];

const SPEC_CHIPS = [
  "Moisture NMT 5.0%",
  "Acid Insoluble Ash NMT 0.5%",
  "Peroxidase Negative",
  "E. coli Absent /g",
  "Salmonella Absent /25g",
  "FSSAI & JECFA Referenced",
];

export const QualityDocs = () => (
  <section className="border-t border-white/10" data-testid="quality-docs-section">
    <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-24 sm:py-32">
      <Reveal>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
          <div>
            <p className="overline-label mb-4">Quality &amp; Documentation</p>
            <h2 className="font-display text-4xl sm:text-6xl font-bold tracking-tighter leading-none">
              Certified,<br />on paper.
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-[#f5f5f5]/60">
            Every batch ships with documentation. Download our master COA and
            MSDS sets covering the full powder and caramel colour range.
          </p>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {DOCS.map((doc, i) => (
          <Reveal key={doc.id} delay={i * 0.1}>
            <div
              className="group relative border border-white/10 p-10 sm:p-12 h-full flex flex-col justify-between transition-colors duration-500 hover:bg-[#111111]"
              data-testid={`doc-card-${doc.id}`}
            >
              <div>
                <div className="flex items-start justify-between mb-14">
                  <doc.icon className="w-8 h-8 text-[#D97736]" strokeWidth={1.5} />
                  <span className="font-mono-jb text-[10px] uppercase tracking-[0.3em] text-[#f5f5f5]/40">
                    {doc.meta}
                  </span>
                </div>
                <p className="font-mono-jb text-xs uppercase tracking-[0.25em] text-[#D97736] mb-3">{doc.code}</p>
                <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-4">{doc.title}</h3>
                <p className="text-sm leading-relaxed text-[#f5f5f5]/60 max-w-md">{doc.desc}</p>
              </div>
              <a
                href={doc.file}
                download
                data-testid={`doc-download-${doc.id}`}
                className="group/btn inline-flex items-center gap-3 mt-10 self-start border border-white/25 text-[#f5f5f5] font-mono-jb text-xs uppercase tracking-[0.15em] font-bold px-7 py-3.5 rounded-full transition-colors duration-300 hover:border-[#D97736] hover:text-[#D97736]"
              >
                <FileText className="w-4 h-4" />
                Download {doc.code}
                <Download className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-y-0.5" />
              </a>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="spec-chips">
          {SPEC_CHIPS.map((chip) => (
            <span
              key={chip}
              className="font-mono-jb text-[10px] uppercase tracking-[0.2em] text-[#f5f5f5]/60 border border-white/15 rounded-full px-5 py-2.5"
            >
              {chip}
            </span>
          ))}
        </div>
        <Link
          to="/quality"
          data-testid="productwise-specs-link"
          className="group inline-flex items-center gap-3 mt-10 bg-[#D97736] text-[#0a0a0a] font-mono-jb text-xs uppercase tracking-[0.15em] font-bold px-8 py-4 rounded-full transition-colors duration-300 hover:bg-[#e8904f]"
        >
          View specs product by product
          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);
